import React, {useState, useEffect} from 'react';
import ArrTreeView from '../../../components/common/arr-tree-view/ArrTreeView';
import {t} from 'i18next';
import {Box, Tabs, Tab, Typography} from '@mui/material';
import ArrIconButton from '../../../components/common/arr-icon-button/ArrIconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './ViewSecurityGroupGrid.scss';
import {useGetAccessControlListData} from '../../../hooks/securityGroup/useSecurityGroup';
import {buildFolderTree} from './transformApiDataToTreeView';
import {
  TreeNode,
  ViewSecurityGroupGridProps,
} from '../../../api/types/securityGroup.types';
import {
  TABS,
  tabToKey,
  tabToSelectedKey,
  userDetailsMoreMenu,
} from '../securityGroupConst';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ANNOTATION_TAB_INDEX, ToastType} from '../../../shared/constants';

const transformAnnotationData = (
  nodes: TreeNode[],
  isLeftDisabled: boolean
): TreeNode[] =>
  nodes.map((node) => ({
    ...node,
    // If it has children, tell ArrTreeView to skip the checkbox
    skipParent: !!(node.children && node.children.length > 0),
    disabled: isLeftDisabled || node.disabled,
    children: node.children
      ? transformAnnotationData(node.children, isLeftDisabled)
      : [],
  }));

const ViewSecurityGroupGrid: React.FC<ViewSecurityGroupGridProps> = ({
  selectedGroup,
  onRightDataChange,
  onTreeViewSelectionFlag,
  refreshKey,
}) => {
  const {
    data: AccessCtrlData,
    error,
    isLoading,
    refetch,
  } = useGetAccessControlListData(selectedGroup || '');

  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>(TABS[0]);

  // Track selections per tab for left tree
  const [leftSelectionPerTab, setLeftSelectionPerTab] = useState<
    Record<string, string[]>
  >(() => Object.fromEntries(TABS.map((tab) => [tab, []])));

  // Right tree selections remain global
  const [selectedRightItems, setSelectedRightItems] = useState<string[]>([]);
  const [leftTreeData, setLeftTreeData] = useState<Record<string, TreeNode[]>>(
    {}
  );
  const [rightTreeData, setRightTreeData] = useState<
    Record<string, TreeNode[]>
  >(Object.fromEntries(TABS.map((tab) => [tab, []])));
  const [_allRightData, setAllRightData] = useState<Record<string, TreeNode[]>>(
    Object.fromEntries(TABS.map((tab) => [tab, []]))
  );
  const [showWarningToast, setShowWarningToast] = useState(false);
  const countLeafNodes = (nodes: TreeNode[]): number => {
    let count = 0;
    nodes.forEach((node) => {
      if (!node.children || node.children.length === 1) {
        count += 1;
      } else {
        count += countLeafNodes(node.children);
      }
    });
    return count;
  };
  const isAnnotationTab = activeTab === TABS[ANNOTATION_TAB_INDEX];
  // Check if there is already an item in the right tree for this tab
  const hasItemInRightTree =
    isAnnotationTab && countLeafNodes(rightTreeData[activeTab] || []) > 0;

  const isInitialLoad = React.useRef(true);
  useEffect(() => {
    if (refreshKey !== undefined) {
      refetch();
    }
  }, [refreshKey]);

  // Track which side is active for selection
  const [activeSide, setActiveSide] = useState<'left' | 'right' | null>(null);

  const enrichNodeData = (nodes: TreeNode[]): TreeNode[] =>
    nodes.map((node) => ({
      ...node,
      parent: node.parent ?? null,
      isGroup: node.isGroup ?? 'false',
      isLocked: node.isLocked ?? false,
      text: node.text ?? node.label ?? '',
      securityGroupId: node.securityGroupId ?? null,
      children: node.children ? enrichNodeData(node.children) : [],
    }));

  // Handle left tree selection per tab
  const handleLeftSelected = (values: string[] | null) => {
    let newSelection = values || [];

    if (isAnnotationTab && newSelection.length > 1) {
      newSelection = [newSelection[newSelection.length - 1]];
    }

    setLeftSelectionPerTab((prev) => ({
      ...prev,
      [activeTab]: newSelection,
    }));

    setActiveSide(newSelection.length > 0 ? 'left' : null);

    if (onTreeViewSelectionFlag) {
      onTreeViewSelectionFlag(
        newSelection.length > 0 || selectedRightItems.length > 0
      );
    }
  };

  const handleRightSelected = (values: string[] | null) => {
    const newSelection = values || [];
    setSelectedRightItems(newSelection);

    setActiveSide(newSelection.length > 0 ? 'right' : null);

    if (onTreeViewSelectionFlag) {
      onTreeViewSelectionFlag(
        (leftSelectionPerTab[activeTab]?.length ?? 0) > 0 ||
          newSelection.length > 0
      );
    }
  };

  const filterTreeWithParents = (
    data: TreeNode[] = [],
    selectedIds: string[]
  ): TreeNode[] =>
    data
      .map((node) => {
        const filteredChildren = node.children
          ? filterTreeWithParents(node.children, selectedIds)
          : [];
        if (selectedIds.includes(node.id) || filteredChildren.length > 0) {
          return {...node, children: filteredChildren};
        }
        return null;
      })
      .filter(Boolean) as TreeNode[];

  // Load data
  useEffect(() => {
    if (!AccessCtrlData) return;
    const apiData = buildFolderTree(AccessCtrlData) || {};
    setLeftTreeData(apiData);

    const initialRightData: Record<string, TreeNode[]> = {};
    for (const tab of TABS) {
      const selectedKey = tabToSelectedKey[tab];
      const selectedNodes = apiData[selectedKey] || [];
      initialRightData[tab] = enrichNodeData(
        filterTreeWithParents(
          apiData[tabToKey[tab]] || [],
          selectedNodes.map((n) => n.id)
        )
      );
    }

    setRightTreeData(initialRightData);
    setSelectedRightItems([]);
    setLeftSelectionPerTab(Object.fromEntries(TABS.map((tab) => [tab, []])));
    setActiveSide(null);
    isInitialLoad.current = false;
  }, [AccessCtrlData]);

  useEffect(() => {
    setAllRightData(rightTreeData);
  }, [rightTreeData]);

  const handleMoveRight = () => {
    const selectedLeftItems = leftSelectionPerTab[activeTab] || [];
    if (!selectedLeftItems.length) return;

    if (isAnnotationTab && hasItemInRightTree) {
      setShowWarningToast(true);
      setLeftSelectionPerTab((prev) => ({
        ...prev,
        [activeTab]: [],
      }));

      // Reset active side to re-enable interactions if needed
      setActiveSide(null);
      return;
    }
    const existingRight = rightTreeData[activeTab];
    const newNodes = filterTreeWithParents(
      leftTreeData[tabToKey[activeTab]] || [],
      selectedLeftItems
    );

    const mergeTrees = (
      existing: TreeNode[] = [],
      addition: TreeNode[] = []
    ) => {
      const map = new Map(existing.map((node) => [node.id, node]));
      for (const node of addition) {
        if (map.has(node.id)) {
          const existingNode = map.get(node.id)!;
          existingNode.children = mergeTrees(
            existingNode.children || [],
            node.children || []
          );
          existingNode.securityGroupId =
            node.securityGroupId ?? existingNode.securityGroupId;
        } else {
          map.set(node.id, node);
        }
      }
      return Array.from(map.values());
    };

    const updatedRight = {
      ...rightTreeData,
      [activeTab]: mergeTrees(existingRight, newNodes),
    };

    setRightTreeData(updatedRight);

    // Clear left selections only for current tab
    setLeftSelectionPerTab((prev) => ({
      ...prev,
      [activeTab]: [],
    }));

    setActiveSide(null); // re-enable both
    if (onRightDataChange) onRightDataChange(updatedRight);
  };

  const handleMoveLeft = () => {
    if (!selectedRightItems.length) return;

    // Capture the IDs being moved back to the left
    const itemsMovingBack = [...selectedRightItems];

    setLeftSelectionPerTab((prev) => ({
      ...prev,
      [activeTab]: [],
    }));

    //Filter out nodes from the right tree
    const removeNodes = (nodes: TreeNode[], isRoot = false): TreeNode[] =>
      nodes
        .map((node) => {
          if (node.children?.length) {
            node.children = removeNodes(node.children, false);
          }
          // If not the root node and the ID is in our selection, remove it
          if (!isRoot && itemsMovingBack.includes(node.id)) return null;
          return node;
        })
        .filter(Boolean) as TreeNode[];

    const updatedRight = {
      ...rightTreeData,
      [activeTab]: removeNodes(rightTreeData[activeTab], true),
    };

    // Update the right tree data and clear the right selection
    setRightTreeData(updatedRight);
    setSelectedRightItems([]); // Reset selection on right side
    setActiveSide(null); // Re-enable interaction for both trees

    if (onRightDataChange) onRightDataChange(updatedRight);
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: (typeof TABS)[number]
  ) => {
    setActiveTab(newValue);
    setActiveSide(null); // keep both trees active
  };

  const getAllExpandedIds = (nodes: TreeNode[] = []): string[] => {
    const ids: string[] = [];

    const traverse = (list: TreeNode[]) => {
      list.forEach((node) => {
        ids.push(node.id);
        if (node.children?.length) {
          traverse(node.children);
        }
      });
    };

    traverse(nodes);
    return ids;
  };

  const getRightTreeIds = (nodes: TreeNode[]): string[] => {
    let ids: string[] = [];
    nodes.forEach((node) => {
      ids.push(node.id);
      if (node.children) {
        ids = [...ids, ...getRightTreeIds(node.children)];
      }
    });
    return ids;
  };

  const rightIdsForActiveTab = getRightTreeIds(rightTreeData[activeTab] || []);

  // Combine the standard selections with the items already moved to the right
  const leftDisplaySelection = Array.from(
    new Set([
      ...(leftSelectionPerTab[activeTab] || []),
      ...rightIdsForActiveTab,
    ])
  );

  const transformLeftTreeData = (
    nodes: TreeNode[],
    movedIds: string[],
    isAnnotation: boolean,
    isRootLevel = true // Used to identify and skip "All Folders"
  ): TreeNode[] => {
    return nodes.map((node) => {
      // Disable if item is in the right tree, but EXCLUDE the root node (All Folders)
      const isMovedToRight = movedIds.includes(node.id);
      const shouldDisable = !isRootLevel && isMovedToRight;

      const transformedNode: TreeNode = {
        ...node,
        disabled: node.disabled || shouldDisable,
      };

      // Apply Annotation-specific logic if needed
      if (isAnnotation) {
        transformedNode.skipParent = !!(
          node.children && node.children.length > 0
        );
      }

      // Recursively transform children
      if (node.children && node.children.length > 0) {
        transformedNode.children = transformLeftTreeData(
          node.children,
          movedIds,
          isAnnotation,
          false // Nested nodes are not root level
        );
      }

      return transformedNode;
    });
  };

  return (
    <div
      className="securityGroup-tab-manager"
      data-testid={`view-security-group-grid-left-treeview-header-right-treeview-header`}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        textColor="inherit"
        indicatorColor="primary"
        className="custom-tabs"
        selectionFollowsFocus
      >
        {TABS.map((tab, index) => (
          <Tab
            key={tab}
            value={tab}
            label={
              <Typography
                className="typo-text subhead4_size"
                data-testid={`view-security-group-grid-tab-name-${index}`}
              >
                {tab}
              </Typography>
            }
          />
        ))}
      </Tabs>
      <div
        className="content"
        data-testid={`view-security-group-grid-left-treeview-header-right-treeview-header-btn`}
      >
        {/* LEFT TREE */}
        <div
          className={`tree-container left-container ${activeSide === 'right' ? 'inactive-tree' : ''}`}
          data-testid={`view-security-group-grid-left-treeview-header`}
        >
          <Box className="left-tree">
            <div
              className="tree-header"
              data-testid={`view-security-group-grid-left-treeview-header-btn`}
            >
              <Typography
                className="typo-text.subhead4_size"
                data-testid={`view-security-group-grid-left-treeview-header-text`}
              >
                {t('securityGroup.leftTreeviewHeader')}
              </Typography>
            </div>
            <ArrTreeView
              itemsList={transformLeftTreeData(
                leftTreeData[tabToKey[activeTab]] || [],
                rightIdsForActiveTab, // Pass the IDs of items already on the right
                isAnnotationTab
              )}
              selectedItem=""
              treeSelectedItems={leftDisplaySelection}
              multipleSelection
              // Ensure expanded IDs are also calculated from the transformed data
              defaultExpandedIds={getAllExpandedIds(
                leftTreeData[tabToKey[activeTab]] || []
              )}
              checkboxGroupSelection={!isAnnotationTab}
              isShowCheckbox
              setSelectedItem={
                activeSide === 'right' ? () => {} : handleLeftSelected
              }
              showActions
              MoreMenuOptions={userDetailsMoreMenu}
              customContentHeight={33}
              handleNoResultMessage={
                isLoading
                  ? t('common.treeItemTransfer.Loading')
                  : t('common.treeItemTransfer.noItemsAvailableText')
              }
            />
          </Box>
        </div>

        {/* ARROWS */}
        <div
          className="arrow-controls"
          data-testid={`view-security-group-grid-div`}
        >
          <ArrIconButton
            size="large"
            ariaLabel="forward-icon"
            className="tranfer-action-icon-button tranfer-action-forward"
            onClick={handleMoveRight}
            icon={<ArrowForwardIcon className="arrow-icon" />}
            shape="default"
            disabled={!(leftSelectionPerTab[activeTab]?.length > 0)}
            testId="view-security-group-grid-forward-icon-icon-button"
          />
          <ArrIconButton
            size="large"
            onClick={handleMoveLeft}
            shape="default"
            ariaLabel="back-icon"
            className="tranfer-action-icon-button"
            icon={<ArrowBackIcon className="arrow-icon" />}
            disabled={!selectedRightItems.length}
            testId="view-security-group-grid-back-icon-icon-button"
          />
        </div>

        {/* RIGHT TREE */}
        <div
          className={`tree-container right-container ${activeSide === 'left' ? 'inactive-tree' : ''}`}
          data-testid={`view-security-group-grid-right-treeview-header`}
        >
          <Box className="right-tree">
            <div
              className="tree-header"
              data-testid={`view-security-group-grid-right-treeview-header-btn`}
            >
              <Typography
                className="typo-text.subhead4_size"
                data-testid={`view-security-group-grid-right-treeview-header-text`}
              >
                {t('securityGroup.rightTreeviewHeader')}
              </Typography>
            </div>
            <ArrTreeView
              key={`right-tree-${activeTab}-${JSON.stringify(rightTreeData[activeTab])?.length}`}
              itemsList={
                isAnnotationTab
                  ? transformAnnotationData(
                      rightTreeData[activeTab] || [],
                      false // Right tree is not affected by the 'isLeftTreeDisabled' logic
                    )
                  : rightTreeData[activeTab] || []
              }
              selectedItem=""
              checkboxGroupSelection={!isAnnotationTab}
              treeSelectedItems={selectedRightItems}
              defaultExpandedIds={getAllExpandedIds(
                isAnnotationTab
                  ? transformAnnotationData(
                      rightTreeData[activeTab] || [],
                      false
                    )
                  : rightTreeData[activeTab] || []
              )}
              multipleSelection
              isShowCheckbox
              setSelectedItem={
                activeSide === 'left' ? () => {} : handleRightSelected
              }
              showActions
              MoreMenuOptions={userDetailsMoreMenu}
              customContentHeight={33}
              handleNoResultMessage={
                error ? t('common.treeItemTransfer.Loading') : ''
              }
            />
          </Box>
        </div>
      </div>
      {showWarningToast && (
        <ArrToastNotification
          title={t('common.warning')}
          message={t('securityGroup.warningMessage')}
          status={[ToastType.Warning]}
          onClose={() => setShowWarningToast(false)}
          setErrorIconAsAlert={true}
        />
      )}
    </div>
  );
};

export default ViewSecurityGroupGrid;
